import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BoardOp } from './board-ops';
import { StudioBoard, StudioChatMessage } from './studio-contract';
import { useStudioApi } from './use-studio-api';

export interface ArdiProposal {
    kind: 'board';
    reply: string;
    ops: BoardOp[];
    render: 'stills' | 'clips' | 'film' | 'none';
    renderShotIds?: string[];
    changedShotIds?: string[];
    suggestions?: string[];
    title?: string;
}

const MAX_CHAT = 60;

function msgId() {
    return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * What Ardi needs to see of the board: the shots as written, and only the
 * state of their pictures — not every past take, which would cost tokens on
 * every turn for nothing.
 */
export function boardForArdi(board: StudioBoard, selectedShotId?: string | null) {
    return {
        format: board.format,
        look: board.look,
        brief: board.brief,
        selectedShotId: selectedShotId || undefined,
        shots: board.shots.map((s, i) => ({
            n: i + 1,
            id: s.id,
            title: s.title,
            kind: s.kind,
            productUuids: s.productUuids,
            scene: s.scene,
            camera: s.camera,
            cameraNote: s.cameraNote,
            framingRef: s.framingRef ? { source: s.framingRef.source } : undefined,
            action: s.action,
            motion: s.motion,
            durationSec: s.durationSec,
            engine: s.engine,
            text: s.text,
            still: s.still?.status || 'none',
            clip: s.clip?.status || 'none',
        })),
    };
}

/**
 * The conversation with Ardi about one production.
 *
 * A turn is an agent run on the `studio-director` profile, watched over its
 * stream. When it ends with a proposal the caller applies it (board ops, then
 * whatever it says to render); when it ends with a question, the options are
 * shown as buttons. The transcript lives on the board so it reopens with it.
 */
export function useArdi(opts: {
    projectUuid?: string;
    board: StudioBoard;
    setBoard: (u: (b: StudioBoard) => StudioBoard) => void;
    selectedShotId?: string | null;
    onProposal: (p: ArdiProposal) => void;
    /** The vendor's brand, so a reference's brand name and logo become theirs. */
    brand?: { name?: string; logoUrl?: string };
}) {
    const { t } = useTranslation();
    const api = useStudioApi();
    const [busy, setBusy] = useState(false);
    const [activity, setActivity] = useState<string | null>(null);
    const streamRef = useRef<AbortController | null>(null);
    const runRef = useRef<string | null>(null);
    const latest = useRef(opts);
    latest.current = opts;

    useEffect(() => () => streamRef.current?.abort(), []);

    const push = useCallback((m: StudioChatMessage) => {
        latest.current.setBoard((b) => ({ ...b, chat: [...(b.chat || []), m].slice(-MAX_CHAT) }));
    }, []);

    const activityFor = useCallback((tool: string) => {
        switch (tool) {
            case 'search_products': return t('STUDIO.ARDI.ACT_SEARCH', 'Looking through your products…');
            case 'inspect_products': return t('STUDIO.ARDI.ACT_INSPECT', 'Studying how they look…');
            case 'analyze_reference': return t('STUDIO.ARDI.ACT_REFERENCE', 'Watching your reference…');
            case 'propose_board': return t('STUDIO.ARDI.ACT_BOARD', 'Writing the shots…');
            case 'ask_vendor': return t('STUDIO.ARDI.ACT_ASK', 'One question…');
            default: return t('STUDIO.ARDI.ACT_THINKING', 'Thinking…');
        }
    }, [t]);

    /** A run has ended: say what it said, and put its board on the board. */
    const finish = useCallback((status: string, result: any) => {
        const r = result || {};
        if (status === 'succeeded' && r.kind === 'board') {
            push({ id: msgId(), role: 'ardi', text: r.reply || '', at: Date.now(), suggestions: r.suggestions });
            latest.current.onProposal(r as ArdiProposal);
        } else if (status === 'succeeded' && r.question) {
            push({ id: msgId(), role: 'ardi', text: r.context || '', at: Date.now(), question: { question: r.question, context: r.context, options: r.options || [] } });
        } else if (status === 'succeeded' && r.reply) {
            push({ id: msgId(), role: 'ardi', text: r.reply, at: Date.now() });
        } else if (status === 'paused') {
            push({ id: msgId(), role: 'ardi', text: t('STUDIO.ARDI.PAUSED', 'That took longer than usual, so I stopped to check with you. Say "continue" to carry on.'), at: Date.now() });
        } else if (status !== 'cancelled') {
            push({ id: msgId(), role: 'ardi', error: true, at: Date.now(), text: t('STUDIO.ARDI.FAILED', 'Something went wrong on my side — nothing was charged. Try again?') });
        }
        latest.current.setBoard((b) => (b.pendingRun ? { ...b, pendingRun: undefined } : b));
    }, [push, t]);

    /** Follow a run until it ends. Resolves when it has, or when the page lets go of it. */
    const follow = useCallback((runId: string) => new Promise<void>((resolve) => {
        let settled = false;
        streamRef.current = api.streamAgentRun(runId, (event) => {
            const d: any = event.data || {};
            if (event.type === 'tool_call' && d.tool) setActivity(activityFor(d.tool));
            if (event.type === 'status' && !settled) {
                settled = true;
                finish(d.status, d.result);
                resolve();
            }
            // The stream ended or broke before the run did (an API restart, a
            // network blip): the run is still going on the server, so ask
            // after it until it ends rather than calling it lost.
            if ((event.type === 'done' || event.type === 'error') && !settled) {
                settled = true;
                poll();
            }
        });
        const poll = async () => {
            for (let i = 0; i < 200; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                if (streamRef.current?.signal.aborted) { resolve(); return; }
                try {
                    const run: any = await api.getAgentRun(runId);
                    if (run?.status && run.status !== 'running') { finish(run.status, run.result); resolve(); return; }
                } catch { /* try again */ }
            }
            push({ id: msgId(), role: 'ardi', error: true, at: Date.now(), text: t('STUDIO.ARDI.LOST', 'I lost the connection. Try again in a moment.') });
            latest.current.setBoard((b) => ({ ...b, pendingRun: undefined }));
            resolve();
        };
    }), [activityFor, api, finish, push, t]);

    // Leaving the page drops the stream, not the run: Ardi carries on on the
    // server. Coming back, pick it up — follow it if it is still going, or
    // apply what it made if it has finished. Before this, a run that ended
    // while the vendor was elsewhere said nothing and changed nothing.
    const resumed = useRef<string | null>(null);
    const pendingRun = opts.board.pendingRun;
    useEffect(() => {
        if (!pendingRun || busy || resumed.current === pendingRun || runRef.current === pendingRun) return;
        resumed.current = pendingRun;
        (async () => {
            setBusy(true);
            setActivity(t('STUDIO.ARDI.ACT_THINKING', 'Thinking…'));
            runRef.current = pendingRun;
            try {
                const run: any = await api.getAgentRun(pendingRun);
                if (run?.status === 'running') await follow(pendingRun);
                else finish(run?.status || 'failed', run?.result);
            } catch {
                latest.current.setBoard((b) => ({ ...b, pendingRun: undefined }));
            } finally {
                setBusy(false);
                setActivity(null);
                runRef.current = null;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingRun]);

    const send = useCallback(async (text: string, extra?: { attachments?: StudioChatMessage['attachments']; products?: Array<{ uuid: string; name: string }> }) => {
        const message = text.trim();
        if (!message || busy) return;
        const { projectUuid, board, selectedShotId } = latest.current;
        push({ id: msgId(), role: 'user', text: message, at: Date.now(), attachments: extra?.attachments });
        setBusy(true);
        setActivity(t('STUDIO.ARDI.ACT_THINKING', 'Thinking…'));
        try {
            const ref = board.reference;
            const run: any = await api.createAgentRun({
                profile: 'studio-director',
                message,
                projectUuid,
                threadId: board.threadId,
                autonomy: 'suggest',
                context: {
                    board: boardForArdi(board, selectedShotId),
                    ...(ref ? { reference: { url: ref.url, kind: ref.kind, cuts: ref.cuts, frames: ref.frames, durationSec: ref.durationSec, analysis: ref.analysis } } : {}),
                    ...(extra?.products?.length ? { attachedProducts: extra.products } : {}),
                    ...(latest.current.brand?.name || latest.current.brand?.logoUrl ? { brand: { name: latest.current.brand.name, hasLogo: !!latest.current.brand.logoUrl } } : {}),
                    language: (navigator.language || 'en').slice(0, 2),
                },
            });
            const runId = run?.runId || run?.id;
            runRef.current = runId;
            resumed.current = runId;
            // Remembered on the board, so leaving the page does not lose it.
            latest.current.setBoard((b) => ({ ...b, pendingRun: runId, threadId: run?.threadId || b.threadId }));
            await follow(runId);
        } catch (err: any) {
            const m = err?.response?.data?.message || err?.message || '';
            push({
                id: msgId(), role: 'ardi', error: true, at: Date.now(),
                text: /credit/i.test(m) ? t('STUDIO.ARDI.NO_CREDITS', 'You are out of credits — top up to keep going.') : t('STUDIO.ARDI.FAILED', 'Something went wrong on my side — nothing was charged. Try again?'),
            });
        } finally {
            setBusy(false);
            setActivity(null);
            runRef.current = null;
        }
    }, [api, busy, follow, push, t]);

    const stop = useCallback(() => {
        if (runRef.current) api.cancelAgentRun(runRef.current).catch(() => undefined);
        streamRef.current?.abort();
        latest.current.setBoard((b) => ({ ...b, pendingRun: undefined }));
        setBusy(false);
        setActivity(null);
    }, [api]);

    return { busy, activity, send, stop, messages: opts.board.chat || [] };
}
